// Postausgang › Druckstapel (#586 Etappe 3): Schreiben mit Zustellweg „Druck“ – drucken (einzeln oder alle als ein
// PDF), von Hand unterschreiben, selbst versenden, abhaken; bei Einschreiben mit Sendungsnummer.
import React, { useCallback, useEffect, useState } from 'react';
import { Check, CircleNotch, Printer } from '@phosphor-icons/react';
import { ImmoButton } from '../../../components/immobilien/ui';
import { druckPdf, ladeDruckstapel, markiereErledigt, type DruckEintrag } from '../../../lib/schreiben/druckstapel';
import { FELD } from '../emails/bereich/Formular';

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

const marke = (text: string, farbe: string, grund: string): React.ReactNode => (
  <span style={{ fontSize: 11.5, fontWeight: 800, padding: '2px 8px', borderRadius: 8, color: farbe, background: grund }}>{text}</span>
);

export function Druckstapel({ karte, titel }: { karte: React.CSSProperties; titel: React.CSSProperties }) {
  const [liste, setListe] = useState<DruckEintrag[] | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [nummer, setNummer] = useState<{ id: string; wert: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const laden = useCallback(() => void ladeDruckstapel().then(setListe), []);
  useEffect(laden, [laden]);

  const drucken = async (auswahl: DruckEintrag[], schluessel: string) => {
    // Fenster sofort öffnen (sonst blockt der Browser das Popup nach dem Laden)
    const fenster = window.open('', '_blank');
    setLaeuft(schluessel); setFehler(null);
    try {
      const url = URL.createObjectURL(await druckPdf(auswahl));
      if (fenster) fenster.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (e) { fenster?.close(); setFehler((e as Error).message); } finally { setLaeuft(null); }
  };

  const erledigt = async (e: DruckEintrag, sendungsnummer: string | null) => {
    setLaeuft(e.id); setFehler(null);
    try { await markiereErledigt(e.id, sendungsnummer); setNummer(null); laden(); }
    catch (err) { setFehler((err as Error).message); } finally { setLaeuft(null); }
  };

  if (!liste?.length) return null; // leer oder Migration fehlt – nichts anzeigen
  return (
    <section className="immo-glas flex flex-col min-w-0" style={karte} aria-label="Druckstapel">
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
        <span style={titel}>Druckstapel fürs Büro ({liste.length})</span>
        <ImmoButton art="akzent" groesse={36} icon={laeuft === 'alle' ? CircleNotch : Printer} disabled={laeuft !== null} onClick={() => void drucken(liste, 'alle')}>
          Alle drucken
        </ImmoButton>
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>Ausdrucken, von Hand unterschreiben, selbst versenden oder einwerfen – dann abhaken. Bei Einschreiben die Sendungsnummer eintragen.</span>
      {fehler && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{fehler}</span>}
      <ul className="m-0 flex flex-col p-0" style={{ gap: 4, listStyle: 'none' }}>
        {liste.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center" style={{ gap: 8, padding: '8px 12px', borderRadius: 12, background: 'var(--fill)' }}>
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
              <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{e.name} · {e.betreff}</span>
              <span className="flex flex-wrap items-center" style={{ gap: 6, fontSize: 12, color: 'var(--ink3)' }}>
                seit {datum(e.seit)}
                {e.schriftform && marke('von Hand unterschreiben', 'var(--warn)', 'var(--warn-soft)')}
                {e.einschreiben && marke(e.einschreiben, 'var(--info)', 'var(--fill2)')}
              </span>
            </span>
            {nummer?.id === e.id ? (
              <span className="flex items-center" style={{ gap: 6 }}>
                <input value={nummer.wert} onChange={(ev) => setNummer({ id: e.id, wert: ev.target.value })} placeholder="Sendungsnummer" aria-label={`Sendungsnummer ${e.name}`}
                  autoFocus style={{ ...FELD, width: 190, height: 36 }} />
                <ImmoButton groesse={36} icon={Check} disabled={laeuft !== null} onClick={() => void erledigt(e, nummer.wert)}>Speichern</ImmoButton>
              </span>
            ) : (
              <span className="flex" style={{ gap: 6 }}>
                <ImmoButton groesse={32} icon={laeuft === `d${e.id}` ? CircleNotch : Printer} disabled={laeuft !== null} onClick={() => void drucken([e], `d${e.id}`)}>Drucken</ImmoButton>
                <ImmoButton groesse={32} icon={laeuft === e.id ? CircleNotch : Check} disabled={laeuft !== null}
                  onClick={() => (e.einschreiben ? setNummer({ id: e.id, wert: '' }) : void erledigt(e, null))}>Erledigt</ImmoButton>
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
